import { LineEndpointType, ObjectType, type EntityConnectionData } from './shared/api_shared_types';
import { setVisibilityState } from './simulate';
import { LINES } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  moveEntityToBack,
  findClosestAttachPoints,
  getAttachpoints,
  getEntityState,
  getZeroStaticEntity,
  removeGameObjectByEntity,
  setParent,
} from './entity';
import { addV2, hasVisibleState, scaleV2, v2 } from './helpers';
import {
  Connection,
  ConnectionEntity,
  VisibilityState,
  GameObjectController,
  RenderItemType,
  V2,
  type GameManager,
  type LineStyle,
  type ServerObjectId,
} from './types';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageBezier,
  type RenderPackageItem,
  type RenderPackageLine,
  type RenderPackagePoint,
} from './render_package';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
  };
}

export function syncConnection(
  manager: GameManager,
  serverId: ServerObjectId,
  connectionData: EntityConnectionData,
) {
  const ent1 = manager.gameObjectServerIdTable[serverId];
  const ent2 = manager.gameObjectServerIdTable[connectionData.entityServerObjId] || null;

  if (ent1) {
    // const type = connectionData.type as ConnectionType;
    const style = getLineSyleByString(connectionData.style);
    // if (type === ConnectionType.LOOSE) {
    const myAttachpoints = connectionData.myAttachpoints?.split(',') || [];
    const otherAttachpoints = connectionData.otherAttachpoints?.split(',') || [];
    const attachpoints1 = myAttachpoints.map((it) => it.trim());
    const attachpoints2 = otherAttachpoints.map((it) => it.trim());
    const conEnt1: ConnectionEntity = {
      serverObjId: serverId,
      ent: ent1,
      attachpoints: attachpoints1,
    };
    const conEnt2: ConnectionEntity = {
      serverObjId: connectionData.entityServerObjId,
      ent: ent2,
      attachpoints: attachpoints2,
    };

    const connection = createAndAddConnection(
      manager,
      conEnt1,
      conEnt2,
      100,
      connectionData.myLineEndPointType,
      connectionData.otherLineEndPointType,
      style,
    );
    // }
  } else {
    logger.error('First entitie to create connection cannot be found', {
      connectionData,
    });
  }
}

export function createAndAddConnection(
  manager: GameManager,
  entity1: ConnectionEntity,
  entity2: ConnectionEntity,
  distance = 100,
  lineEndPointType1: LineEndpointType,
  lineEndPointType2: LineEndpointType,
  style = LINES.DEFAULT,
): Connection {
  if (!entity1 && !entity2) {
    return;
  }

  const parent = entity1.ent.parent || entity2.ent.parent;

  let connection: Connection = {
    ...getZeroStaticEntity(),
    objectType: ObjectType.CONNECTION,
    entities: [entity1, entity2],
    maxDistance: distance,
    minDistance: 0,
    style: style,
    hidden: false,
    alpha: 0,
    lineEndPointType1,
    lineEndPointType2,
  };

  addGameObject(manager, connection);

  setParent(connection, parent, v2());
  moveEntityToBack(connection);
  connection.fixedZIndex = true;

  if (connection.entities[0].ent && connection.entities[1].ent) {
    setVisibilityState(connection, VisibilityState.APPEARING);
  }

  return connection;
}

export function updateConnections(connections: Connection[]) {
  for (let i = 0; i < connections.length; i++) {
    const connection = connections[i];
    update(connection);
  }
}

function update(obj: Connection): boolean {
  const ent1 = obj.entities[0].ent;
  const ent2 = obj.entities[1].ent;

  if ((!ent1 || ent1.doKill) && (!ent2 || ent2.doKill)) {
    removeGameObjectByEntity(obj);
    return;
  }

  for (let i = 0; i < obj.entities.length; i++) {
    const connEnt = obj.entities[i];

    if (!connEnt.ent && connEnt.serverObjId) {
      const ent = ent1.manager.gameObjectServerIdTable[connEnt.serverObjId];
      if (ent) {
        connEnt.ent = ent;
        if (obj.entities[0].ent && obj.entities[1].ent) {
          setVisibilityState(obj, VisibilityState.APPEARING);
        }
      }
    }
  }

  const state = obj.visibilityState;

  if (state === VisibilityState.APPEARING) {
    obj.alpha = obj.stateProgress;
  } else if (state === VisibilityState.DISAPPEARING) {
    obj.alpha = 1 - obj.stateProgress;
  } else if (obj.entityStateChanged) {
    if (state === VisibilityState.DEFAULT) {
      obj.alpha = 1;
    } else if (state === VisibilityState.HIDDEN) {
      obj.alpha = 0;
    }
  }

  if (ent1 && ent2) {
    const state1 = getEntityState(ent1);
    const state2 = getEntityState(ent2);

    if (
      state1 === VisibilityState.HIDING ||
      state1 === VisibilityState.HIDDEN ||
      state2 === VisibilityState.HIDING ||
      state2 === VisibilityState.HIDDEN
    ) {
      if (hasVisibleState(obj.visibilityState)) {
        setVisibilityState(obj, VisibilityState.HIDDEN);
      }
    } else if (state1 === VisibilityState.DISAPPEARING || state2 === VisibilityState.DISAPPEARING) {
      setVisibilityState(obj, VisibilityState.DISAPPEARING);
    } else if (!hasVisibleState(obj.visibilityState)) {
      setVisibilityState(obj, VisibilityState.APPEARING);
    }

    if (obj.parent !== ent1.parent && ent1.parent) {
      setParent(obj, ent1.parent, v2());
      moveEntityToBack(obj);
    }
  }

  return false;
}

export function getBezierConnectionLine(entA: ConnectionEntity, entB: ConnectionEntity): V2[] {
  let points: V2[] = [v2(), v2(), v2(), v2()];

  const pointsA = getAttachpoints(entA.ent, entA.attachpoints);

  const pointsB = getAttachpoints(entB.ent, entB.attachpoints);

  if (pointsA.length && pointsB.length) {
    const [pointA, pointB] = findClosestAttachPoints(pointsA, pointsB);

    const diffVert = Math.abs(pointA.pos.x - pointB.pos.x);
    const diffHor = Math.abs(pointA.pos.y - pointB.pos.y);

    if (!pointA || !pointB) return;

    points[0] = pointA.pos;

    let controlPointADist = 80;
    if (Math.abs(pointA.dir.x) === 1) {
      controlPointADist = Math.max(diffVert, diffHor * 0.7) / 1.7;
    } else {
      controlPointADist = Math.max(diffVert * 0.7, diffHor) / 1.7;
    }
    const diff1 = scaleV2(pointA.dir, Math.max(40, controlPointADist));
    points[1] = addV2(points[0], diff1);

    points[3] = pointB.pos;

    let controlPointBDist = 80;
    if (Math.abs(pointB.dir.x) === 1) {
      controlPointBDist = Math.max(diffVert, diffHor * 0.7) / 1.7;
    } else {
      controlPointBDist = Math.max(diffVert * 0.7, diffHor) / 1.7;
    }
    const diff2 = scaleV2(pointB.dir, Math.max(40, controlPointBDist));
    points[2] = addV2(points[3], diff2);
  }

  return points;
}

export function createPackage(
  connection: Connection,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (connection.style.width > 0) {
    const conEntA = connection.entities[0];
    const conEntB = connection.entities[1];
    const entA = connection.entities[0].ent;
    const entB = connection.entities[1].ent;
    const posA = addV2(position, scaleV2(entA.pos, scale));
    const posB = addV2(position, scaleV2(entB.pos, scale));

    if (connection.style.bezier) {
      const points = getBezierConnectionLine(conEntA, conEntB);
      let b: RenderPackageBezier = {
        type: RenderItemType.bezier,
        ...EMPTY_RENDER_TRANSFORM,

        posA: addV2(position, scaleV2(points[0], scale)),
        cpA: addV2(position, scaleV2(points[1], scale)),
        cpB: addV2(position, scaleV2(points[2], scale)),
        posB: addV2(position, scaleV2(points[3], scale)),
        style: connection.style,
        opacity: connection.alpha,
      };
      rPackage.push(b);

      if (false) {
        for (let i = 0; i < 4; i++) {
          let p: RenderPackagePoint = {
            type: RenderItemType.point,
            ...EMPTY_RENDER_TRANSFORM,
            positionX: points[i].x,
            positionY: points[i].y,
            size: 10,
            color: COLORS.RED_SALSA,
          };
        }
      }
    } else {
      let line: RenderPackageLine = {
        type: RenderItemType.line,
        ...EMPTY_RENDER_TRANSFORM,
        posA: posA,
        posB: posB,
        style: connection.style,
        opacity: connection.alpha,
      };
      rPackage.push(line);
    }

    // if (connection.lineEndPointType1 === LineEndpointType.DOT) {
    //   const dot = buildLineDot(
    //     position,
    //     1,
    //     posA.x,
    //     posA.y,
    //     connection.style.color,
    //     connection.style.width,
    //   );
    //   logger.merijn('push dot', dot);
    //   rPackage.push(dot);
    // }
    // if (connection.lineEndPointType2 === LineEndpointType.DOT) {
    //   rPackage.push(
    //     buildLineDot(
    //       position,
    //       scale,
    //       posB.x,
    //       posB.y,
    //       connection.style.color,
    //       connection.style.width,
    //     ),
    //   );
    // }
  }

  return rPackage;
}

export function getLineSyleByString(lineStyleString: string): LineStyle {
  switch (lineStyleString) {
    case 'reframe_negative':
      return { ...LINES.REFRAME_NEGATIVE };

    case 'reframe_positive':
      return { ...LINES.REFRAME_POSITIVE };

    case 'normal':
      return { ...LINES.DEFAULT };

    case 'thin':
      return { ...LINES.THIN };

    case 'dotted':
      return { ...LINES.DOTTED };

      case 'blue':
      return { ...LINES.BLUE };

    case 'thick':
      return { ...LINES.THICK };

    default:
      return { ...LINES.THIN };
  }
}
